import type { BoltOnDischargeDataContext, CrossSectionDataSet } from "./d";
import {
  generateEquipmentDataPoints,
  generateCrossSectionDataSetTitle,
  generateMLEDataPoints
} from "./calculateCrossSectionDataSets";
import { convertSmallLengthUnits } from "@/libs/units/length";
import { getYToolBypassPositioning } from "./getYToolChartDataSet";

/**
 * Generates a dataset representing the bolt-on discharge cross-section
 *
 * @param boltOnDischargeDataContext The cross-section chart context
 * @returns A dataset representing the bolt-on discharge cross-section
 */
export const getBoltOnDischargeChartDataSet = (
  boltOnDischargeDataContext: BoltOnDischargeDataContext
): CrossSectionDataSet[] => {
  const boltOnDischargeChartDataSet: CrossSectionDataSet[] = [];
  const casingInnerRadius =
    boltOnDischargeDataContext.casing.innerDiameterIn / 2;
  const casingInnerPoints = generateEquipmentDataPoints(
    casingInnerRadius,
    0,
    0
  );
  const casingDriftRadius =
    boltOnDischargeDataContext.casing.driftDiameterIn / 2;
  const casingDriftPoints = generateEquipmentDataPoints(
    casingDriftRadius,
    0,
    0
  );
  boltOnDischargeChartDataSet.push({
    title: generateCrossSectionDataSetTitle(
      "Casing ID",
      boltOnDischargeDataContext.casing.innerDiameterIn,
      boltOnDischargeDataContext.lengthUnit
    ),
    xAxisLabel: `${boltOnDischargeDataContext.lengthUnit.label}`,
    yAxisLabel: `${boltOnDischargeDataContext.lengthUnit.label}`,
    points: casingInnerPoints.map(pt => {
      return {
        x: convertSmallLengthUnits(pt.x, boltOnDischargeDataContext.lengthUnit),
        y: convertSmallLengthUnits(pt.y, boltOnDischargeDataContext.lengthUnit)
      };
    })
  });
  boltOnDischargeChartDataSet.push({
    title: generateCrossSectionDataSetTitle(
      "Casing Drift ID",
      boltOnDischargeDataContext.casing.driftDiameterIn,
      boltOnDischargeDataContext.lengthUnit
    ),
    xAxisLabel: `${boltOnDischargeDataContext.lengthUnit.label}`,
    yAxisLabel: `${boltOnDischargeDataContext.lengthUnit.label}`,
    points: casingDriftPoints.map(pt => {
      return {
        x: convertSmallLengthUnits(pt.x, boltOnDischargeDataContext.lengthUnit),
        y: convertSmallLengthUnits(pt.y, boltOnDischargeDataContext.lengthUnit)
      };
    })
  });

  const dischargeDimRadius = boltOnDischargeDataContext.outerDiameterIn / 2;
  const maxEquipmentOuterDiameterIn =
    boltOnDischargeDataContext.maxEquipmentOuterDiameterIn || 0;
  let dischargeDimCenterX: number;

  if (
    boltOnDischargeDataContext.hasYTool &&
    boltOnDischargeDataContext.bypassTubingOuterDiameterIn &&
    boltOnDischargeDataContext.yToolWidthIn
  ) {
    const bypassPositioning = getYToolBypassPositioning({
      casingDriftRadius,
      yToolWidthIn: boltOnDischargeDataContext.yToolWidthIn,
      bypassTubingOuterDiameterIn:
        boltOnDischargeDataContext.bypassTubingOuterDiameterIn,
      lengthUnit: boltOnDischargeDataContext.lengthUnit
    });
    boltOnDischargeChartDataSet.push(bypassPositioning.bypassTubingDataSet);

    const maxEquipmentDimRadius = maxEquipmentOuterDiameterIn / 2;
    dischargeDimCenterX =
      bypassPositioning.bypassTubingDimCenterX +
      bypassPositioning.bypassTubingDimRadius +
      maxEquipmentDimRadius;
  } else {
    dischargeDimCenterX = -casingDriftRadius + maxEquipmentOuterDiameterIn / 2;
  }

  const dischargeDimPoints = generateEquipmentDataPoints(
    dischargeDimRadius,
    dischargeDimCenterX,
    0
  );
  boltOnDischargeChartDataSet.push({
    title: generateCrossSectionDataSetTitle(
      "Bolt On Discharge OD",
      boltOnDischargeDataContext.outerDiameterIn,
      boltOnDischargeDataContext.lengthUnit
    ),
    xAxisLabel: `${boltOnDischargeDataContext.lengthUnit.label}`,
    yAxisLabel: `${boltOnDischargeDataContext.lengthUnit.label}`,
    points: dischargeDimPoints.map(pt => {
      return {
        x: convertSmallLengthUnits(pt.x, boltOnDischargeDataContext.lengthUnit),
        y: convertSmallLengthUnits(pt.y, boltOnDischargeDataContext.lengthUnit)
      };
    })
  });

  if (!boltOnDischargeDataContext.hasYTool) {
    const mleDimRadius = boltOnDischargeDataContext.mle.depthIn / 2;
    const mleDimCenterX =
      dischargeDimRadius + mleDimRadius + dischargeDimCenterX;
    const mleUpperDimCenterY =
      boltOnDischargeDataContext.mle.widthIn / 2 - mleDimRadius;
    const mleDimPoints = generateMLEDataPoints(
      mleDimRadius,
      mleDimCenterX,
      mleUpperDimCenterY,
      mleDimRadius,
      mleDimCenterX,
      -mleUpperDimCenterY
    );
    boltOnDischargeChartDataSet.push({
      title: generateCrossSectionDataSetTitle(
        "MLE Depth",
        boltOnDischargeDataContext.mle.depthIn,
        boltOnDischargeDataContext.lengthUnit
      ),
      xAxisLabel: `${boltOnDischargeDataContext.lengthUnit.label}`,
      yAxisLabel: `${boltOnDischargeDataContext.lengthUnit.label}`,
      points: mleDimPoints.map(pt => ({
        x: convertSmallLengthUnits(pt.x, boltOnDischargeDataContext.lengthUnit),
        y: convertSmallLengthUnits(pt.y, boltOnDischargeDataContext.lengthUnit)
      }))
    });

    const dischargeAndMleDimRadius =
      boltOnDischargeDataContext.maxCalculatedOuterDiameterIn / 2;
    const dischargeAndMleDimCenterX =
      dischargeAndMleDimRadius - dischargeDimRadius + dischargeDimCenterX;
    const dischargeAndMleDimPoints = generateEquipmentDataPoints(
      dischargeAndMleDimRadius,
      dischargeAndMleDimCenterX,
      0
    );
    boltOnDischargeChartDataSet.push({
      title: generateCrossSectionDataSetTitle(
        "Discharge + MLE OD",
        boltOnDischargeDataContext.maxCalculatedOuterDiameterIn,
        boltOnDischargeDataContext.lengthUnit
      ),
      xAxisLabel: `${boltOnDischargeDataContext.lengthUnit.label}`,
      yAxisLabel: `${boltOnDischargeDataContext.lengthUnit.label}`,
      points: dischargeAndMleDimPoints.map(pt => ({
        x: convertSmallLengthUnits(pt.x, boltOnDischargeDataContext.lengthUnit),
        y: convertSmallLengthUnits(pt.y, boltOnDischargeDataContext.lengthUnit)
      }))
    });
  }

  return boltOnDischargeChartDataSet;
};
